import { useState } from "react";
import { adminApi } from "@/lib/api/endpoints";
import { useAsync } from "@/lib/hooks/useAsync";
import { Button, Card, CardSection, Field, Input, Spinner, Textarea, useToast } from "@/components/ui";
import { useAuth } from "@/features/auth/AuthContext";
import { formatDateTime } from "@/lib/utils/format";
import { PaymentsSettings } from "./PaymentsSettings";

/**
 * Organization settings, including the config defaults every event inherits.
 *
 * Defaults are edited as raw JSON: they're an arbitrary partial of the event
 * config, and a bespoke form for every possible key would go stale the moment
 * the schema grows.
 */
export function OrganizationPage() {
  const { organization, user } = useAuth();
  const toast = useToast();
  const request = useAsync(
    () => (organization ? adminApi.organizations.get(organization.id) : Promise.reject(new Error("No organization"))),
    [organization?.id],
  );

  const [defaults, setDefaults] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [jsonError, setJsonError] = useState<string | null>(null);

  if (request.loading) return <div className="grid py-20 place-items-center"><Spinner size={24} className="text-accent" /></div>;
  if (!request.data) return <p className="text-sm text-danger">{request.error?.message ?? "Could not load organization"}</p>;

  const org = request.data;
  const defaultsText = defaults ?? JSON.stringify(org.default_config ?? {}, null, 2);
  const isSuperadmin = user?.role === "superadmin";

  async function save() {
    let parsed: Record<string, unknown>;
    try {
      parsed = JSON.parse(defaultsText);
    } catch (error) {
      setJsonError(error instanceof Error ? error.message : "Invalid JSON");
      return;
    }

    setJsonError(null);
    setSaving(true);
    try {
      await adminApi.organizations.update(org.id, { default_config: parsed });
      toast.success("Organization defaults saved");
      request.reload();
      setDefaults(null);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not save");
    } finally {
      setSaving(false);
    }
  }

  const used = org.generations_used ?? 0;
  const limit = org.generations_limit ?? 0;

  return (
    <div className="flex flex-col gap-8">
      <CardSection eyebrow="Account" title="Organization" description="Shared settings for every event under this tenant.">
        <Card className="grid gap-5 p-5 sm:grid-cols-2">
          <Field label="Name">
            <Input
              defaultValue={org.name}
              onBlur={(e) => {
                if (e.target.value === org.name) return;
                void adminApi.organizations
                  .update(org.id, { name: e.target.value })
                  .then(() => { toast.success("Saved"); request.reload(); })
                  .catch((error: Error) => toast.error(error.message));
              }}
            />
          </Field>
          <Field label="Slug" hint={isSuperadmin ? undefined : "Only a superadmin can change this."}>
            <Input className="font-mono" defaultValue={org.slug} disabled={!isSuperadmin} />
          </Field>
        </Card>
      </CardSection>

      <CardSection eyebrow="Brand" title="White label" description="What guests see. Nothing about Jeba reaches an attendee.">
        <Card className="grid gap-5 p-5 sm:grid-cols-2">
          <Field label="Booth subdomain" hint={org.booth_host ? `Booth links: ${org.booth_host}/event-slug` : "Set this to get your own booth host."}>
            <Input className="font-mono" defaultValue={org.subdomain ?? ""} placeholder="yourbrand" disabled={!isSuperadmin} />
          </Field>
          <Field label="Brand name" hint="Shown in the booth's title bar and diagnostics.">
            <Input defaultValue={org.white_label?.name ?? org.name} disabled />
          </Field>
        </Card>
      </CardSection>

      <PaymentsSettings organization={org} onSaved={request.reload} />

      <CardSection eyebrow="Usage" title="Generation quota">
        <Card className="flex flex-col gap-4 p-5">
          <div className="flex flex-wrap items-end gap-4">
            <div>
              <span className="text-3xl font-bold tabular-nums">{used.toLocaleString()}</span>
              <span className="ml-2 text-sm text-haze-400">of {limit.toLocaleString()} used</span>
            </div>
            <span className="ml-auto text-xs text-haze-400">
              Resets {org.generations_reset_at ? formatDateTime(org.generations_reset_at) : "at the start of each month"}
            </span>
          </div>
          <div className="h-1.5 overflow-hidden rounded-full bg-ink-600">
            <div
              className="h-full rounded-full bg-gradient-to-r from-aqua to-accent"
              style={{ width: `${limit > 0 ? Math.min(100, (used / limit) * 100) : 0}%` }}
            />
          </div>
          <p className="text-xs text-haze-400">
            {isSuperadmin
              ? "Quota limits are set per organization and are a commercial setting."
              : "Contact your account manager to change this limit."}
          </p>
        </Card>
      </CardSection>

      <CardSection
        eyebrow="Defaults"
        title="Event config defaults"
        description="Merged underneath every event's own configuration. Use it for house branding or a default AI provider, so a new event starts on-brand."
      >
        <Card className="flex flex-col gap-4 p-5">
          <Field
            label="Default config (JSON)"
            error={jsonError ?? undefined}
            hint='Example: {"event":{"provider":"bfl","branding":{"primary_color":"#d4f14f"}}}'
          >
            <Textarea
              rows={14}
              spellCheck={false}
              className="font-mono text-xs"
              value={defaultsText}
              onChange={(e) => { setDefaults(e.target.value); setJsonError(null); }}
            />
          </Field>
          <div className="flex justify-end gap-2">
            {defaults !== null && (
              <Button variant="ghost" onClick={() => { setDefaults(null); setJsonError(null); }}>Discard</Button>
            )}
            <Button variant="primary" loading={saving} disabled={defaults === null} onClick={save}>
              Save defaults
            </Button>
          </div>
        </Card>
      </CardSection>
    </div>
  );
}
